import { useFirestore } from "hooks/firestore/v2/useFirestore";
import { tokenAutomation } from "hooks/firestore/v2/models/tokenAutomation";
import { tokenAllocation } from "hooks/firestore/v2/models/tokenAllocation";
import { useMemo } from "react";
import { isAllocationAllowed } from "utilities/promotions";
import { betslipToSelectionsContext } from "utilities/promotions/transformers/betslipToSelectionsContext";
import { betslipToUsagesContext } from "utilities/promotions/transformers/betslipToUsagesContext";
import { betslipToUsedAllocations } from "utilities/promotions/transformers/betslipToUsedAllocationIds";
import { useSelector } from "hooks";
import { markAsUsed } from "utilities/promotions/markAsUsed";
import type { Always } from "types/utilities";

const useTokenAllocations = (outcomeIds: string[]) => {
  const { data: automations, isLoading: automationsLoading } = useFirestore(
    "/tokenAutomations",
    tokenAutomation.array(),
  );

  const { data: allocations, isLoading: allocationsLoading } = useFirestore(
    "/users/{userId}/tokenAllocations",
    tokenAllocation.array(),
    {
      keyPath: "id",
    },
  );

  const betslipState = useSelector((state) => state.betslip);
  const selectionContext = betslipToSelectionsContext(betslipState, outcomeIds);
  const usageContext = betslipToUsagesContext(
    betslipState,
    allocations,
    automations,
    selectionContext,
  );

  const usedAllocations = betslipToUsedAllocations(betslipState);

  const filteredAllocations = useMemo(() => {
    if (!allocations || !automations) return [];

    return (allocations || [])
      .filter((allocation) => {
        const automation = automations.find(
          (automation) => allocation.automationId === automation.automationId,
        )!;

        return isAllocationAllowed({
          allocation,
          automation,
          selection: selectionContext,
          usages: usageContext,
          usedAllocations,
          allocations,
        });
      })
      .map((allocation) => {
        const automation = automations.find(
          (automation) => allocation.automationId === automation.automationId,
        )!;

        return markAsUsed({
          allocation,
          automation,
          selection: selectionContext,
          usages: usageContext,
          usedAllocations,
          allocations,
        });
      });
  }, [allocations, selectionContext, automations]);

  return {
    isLoading: automationsLoading || allocationsLoading,
    allocations: filteredAllocations,
    automations: automations,
    usedAllocations,
  } as const;
};

type TokenAllocations = Always<
  ReturnType<typeof useTokenAllocations>["allocations"]
>;

export type { TokenAllocations };
export { useTokenAllocations };
